import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAppData } from '@/context/AppDataContext';
import { useAuth } from '@/context/AuthContext';
import { formatCurrency as fmtCur } from '@/lib/market';
import {
  TrendingUp, TrendingDown, BarChart3, Activity, Shield,
  RefreshCw, AlertTriangle, Target, Wallet, ArrowUpRight, ArrowDownRight,
  Layers, Building2, Briefcase, CircleDollarSign,
  LineChart as LineIcon, PieChart as PieIcon, Lightbulb, Bell,
  Globe, Lock, Rocket
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import SmartInsightsPage from './SmartInsightsPage';
import AlertsPage from './AlertsPage';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PM_CATS  = ['vc','pe','search','long_only','credit','infra','hedge_fund','secondaries','special_situations','pre_ipo','mezzanine','fof','co_investment','royalty','natural_resources'];
const ALT_CATS = ['gold','art','cash','alternatives','ip'];

const ALLOC_LABEL_MAP = {
  stocks: 'Equities', mf: 'Mutual Funds', fd: 'Fixed Deposits',
  bonds: 'Bonds', etf: 'ETFs', fo: 'Futures & Options',
  nps: 'National Pension System', pms: 'Portfolio Mgmt. Services',
  aif: 'Alternative Inv. Funds', ppf: 'PPF / EPF',
  gold: 'Gold / Silver', realestate: 'Real Estate',
};

// VAIA Chart Color Palette — warm earth tones
const CHART_COLORS = [
  '#ffd87a',  // gold
  '#e8a838',  // amber gold
  '#c97a3a',  // amber brown
  '#a86830',  // deep amber
  '#d4956a',  // clay
  '#d9c4a0',  // warm linen
  '#b8a070',  // taupe
  '#8ba888',  // muted sage
];

// fmt is set per-component instance via makeFmt(market)
const makeFmt = (market) => (v) => {
  if (v === null || v === undefined) return '—';
  return fmtCur(v, market);
};

const pct = (v) => {
  if (v === null || v === undefined) return '—';
  return `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;
};

// ── Donut Chart ───────────────────────────────────────────────────────────────
const DonutChart = ({ data, size = 190 }) => {
  if (!data?.length) return null;
  const total = data.reduce((s, d) => s + d.value, 0);
  if (!total) return null;
  const cx = size / 2, cy = size / 2, r = size / 2 - 14;
  const toR = (a) => (a * Math.PI) / 180;
  let angle = -90;
  const segs = data.map((item, i) => {
    const sweep = (item.value / total) * 360;
    if (sweep < 0.5) return null;
    const sa = angle, ea = angle + sweep;
    angle = ea;
    const x1 = cx + r * Math.cos(toR(sa)), y1 = cy + r * Math.sin(toR(sa));
    const x2 = cx + r * Math.cos(toR(ea)), y2 = cy + r * Math.sin(toR(ea));
    const large = sweep > 180 ? 1 : 0;
    return (
      <path
        key={i}
        d={`M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`}
        fill={item.color}
        stroke="#ffffff"
        strokeWidth="2"
        className="transition-opacity hover:opacity-80"
      />
    );
  });
  return (
    <svg width={size} height={size} className="mx-auto shrink-0">
      {segs}
      <circle cx={cx} cy={cy} r={r * 0.52} fill="#ffffff" />
    </svg>
  );
};

// ── NAV Line Chart ─────────────────────────────────────────────────────────────
const NavLineChart = ({ data, height = 220, width = 800 }) => {
  if (!data || data.length < 2) return null;
  const pad = { t: 16, r: 16, b: 28, l: 64 };
  const cw = width - pad.l - pad.r;
  const ch = height - pad.t - pad.b;
  const vals = data.map(d => d.nav || d.value || 0);
  const minV = Math.min(...vals) * 0.985;
  const maxV = Math.max(...vals) * 1.015;
  const xs = (i) => pad.l + (i / (data.length - 1)) * cw;
  const ys = (v) => pad.t + ch - ((v - minV) / (maxV - minV || 1)) * ch;
  const lineD = data.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xs(i)} ${ys(p.nav || p.value || 0)}`).join(' ');
  const areaD = `${lineD} L ${xs(data.length - 1)} ${pad.t + ch} L ${pad.l} ${pad.t + ch} Z`;
  const yLabels = [minV, (minV + maxV) / 2, maxV];
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-visible">
      <defs>
        <linearGradient id="vaiaGrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#ffd879" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#ffd879" stopOpacity="0" />
        </linearGradient>
      </defs>
      {yLabels.map((v, i) => (
        <g key={i}>
          <line x1={pad.l} y1={ys(v)} x2={width - pad.r} y2={ys(v)} stroke="#e8e3d8" strokeDasharray="4" />
          <text x={pad.l - 8} y={ys(v)} textAnchor="end" dominantBaseline="middle" fontSize="10" fill="#7a6a58">
            {v.toFixed(2)}
          </text>
        </g>
      ))}
      <path d={areaD} fill="url(#vaiaGrad)" />
      <path d={lineD} fill="none" stroke="#ffd879" strokeWidth="2.5" strokeLinejoin="round" />
      {[0, Math.floor(data.length / 2), data.length - 1].map((i) => (
        <text key={i} x={xs(i)} y={height - 4} textAnchor="middle" fontSize="10" fill="#7a6a58">
          {data[i]?.date?.split('T')[0]?.slice(5) || ''}
        </text>
      ))}
    </svg>
  );
};

// ── Card Header ───────────────────────────────────────────────────────────────
const NavHead = ({ icon: Icon, title, sub, extra, amber }) => (
  <div className={`${amber ? 'bg-[#ffd879]' : 'bg-[#dae4c4]'} px-5 py-3 flex items-center justify-between`}>
    <div>
      <h3 className={`text-sm font-semibold flex items-center gap-2 ${amber ? 'text-black' : 'text-[#1a1a1a]'}`}>
        <Icon className="w-4 h-4" /> {title}
      </h3>
      {sub && <p className={`text-xs ${amber ? 'text-black/60' : 'text-[#4a4a4a]'}`}>{sub}</p>}
    </div>
    {extra}
  </div>
);

// ── Main Component ────────────────────────────────────────────────────────────
export default function AnalyticsDashboardPage() {
  const { user } = useAuth();
  const { assetClassMap } = useAppData();
  const fmt = makeFmt(user?.market);
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('ytd');
  const isFirstLoad = useRef(true);

  // Static data (loaded once)
  const [consolidation, setConsolidation]     = useState(null);
  const [sectorExposure, setSectorExposure]   = useState(null);
  const [marketCapExp, setMarketCapExp]       = useState(null);
  const [risk, setRisk]                       = useState(null);
  const [navData, setNavData]                 = useState(null);

  // Period-sensitive data
  const [performance, setPerformance]   = useState(null);
  const [attribution, setAttribution]   = useState(null);
  const [benchmarks, setBenchmarks]     = useState([]);

  // Public markets enrichment + private markets data
  const [assetClassSummary, setAssetClassSummary] = useState([]);
  const [pmVC, setPmVC]     = useState([]);
  const [pmPE, setPmPE]     = useState([]);
  const [pmDeals, setPmDeals] = useState([]);
  const [totalFMV, setTotalFMV] = useState(0);

  const fetchStatic = async () => {
    const [cRes, sRes, mRes, rRes, pRes, acRes, vcRes, peRes, dealsRes, reRes, pmResults, altResults] = await Promise.all([
      axios.get(`${API}/analytics/consolidation`),
      axios.get(`${API}/analytics/exposure/sector/detailed`),
      axios.get(`${API}/analytics/exposure/market-cap/detailed`),
      axios.get(`${API}/analytics/risk`),
      axios.get(`${API}/markets/portfolios`).catch(() => ({ data: [] })),
      axios.get(`${API}/analytics/exposure/asset_class`).catch(() => ({ data: { items: [] } })),
      axios.get(`${API}/private-markets/vc`).catch(() => ({ data: [] })),
      axios.get(`${API}/private-markets/pe`).catch(() => ({ data: [] })),
      axios.get(`${API}/deals/`).catch(() => ({ data: [] })),
      axios.get(`${API}/real-estate/portfolio`).catch(() => ({ data: null })),
      Promise.all(PM_CATS.map(c => axios.get(`${API}/private-markets/${c}`).then(r => r.data || []).catch(() => []))),
      Promise.all(ALT_CATS.map(c => axios.get(`${API}/other-assets/${c}`).then(r => r.data || []).catch(() => []))),
    ]);
    const pubData = cRes.data;
    setConsolidation(pubData);
    setSectorExposure(sRes.data);
    setMarketCapExp(mRes.data);
    setRisk(rRes.data);
    setAssetClassSummary(acRes.data?.items || []);
    setPmVC(vcRes.data || []);
    setPmPE(peRes.data || []);
    setPmDeals(dealsRes.data || []);

    // Compute total FMV across all asset classes
    const pmAUM  = pmResults.flat().reduce((s, d) => s + (d.current_value || d.nav || 0), 0);
    const altObj = Object.fromEntries(ALT_CATS.map((c, i) => [c, altResults[i]]));
    const goldV  = (altObj.gold || []).reduce((s, d) => s + (d.units || 0) * (d.current_price || 0), 0);
    const artV   = (altObj.art  || []).reduce((s, d) => s + (d.current_value || 0), 0);
    const cashV  = (altObj.cash || []).reduce((s, d) => s + (d.balance || 0), 0);
    const altV   = (altObj.alternatives || []).reduce((s, d) => s + (d.nav || 0), 0);
    const ipV    = (altObj.ip   || []).reduce((s, d) => s + (d.current_value || 0), 0);
    const reVal  = reRes.data?.total_value || 0;
    setTotalFMV((pubData?.total_value || 0) + pmAUM + goldV + artV + cashV + altV + ipV + reVal);

    if (pRes.data.length > 0) {
      try {
        const nRes = await axios.get(`${API}/analytics/nav/${pRes.data[0].id}`);
        setNavData(nRes.data);
      } catch {}
    }
  };

  const fetchPeriod = async (p) => {
    const [perf, attr, bench] = await Promise.all([
      axios.get(`${API}/analytics/performance?period=${p}`),
      axios.get(`${API}/analytics/attribution?period=${p}`),
      axios.get(`${API}/analytics/performance/all-benchmarks?period=${p}`),
    ]);
    setPerformance(perf.data);
    setAttribution(attr.data);
    setBenchmarks(bench.data);
  };

  // Initial load — runs once
  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        await Promise.all([fetchStatic(), fetchPeriod(period)]);
      } catch {
        setError('Failed to load analytics data. Please try again.');
      } finally {
        setLoading(false);
        isFirstLoad.current = false;
      }
    })();
  }, []); // eslint-disable-line

  // Period change — only re-fetches period-sensitive data, no full spinner
  useEffect(() => {
    if (isFirstLoad.current) return;
    (async () => {
      setRefreshing(true);
      try { await fetchPeriod(period); } catch {}
      setRefreshing(false);
    })();
  }, [period]); // eslint-disable-line

  const handleRefresh = async () => {
    setRefreshing(true);
    try { await Promise.all([fetchStatic(), fetchPeriod(period)]); } catch {}
    setRefreshing(false);
  };

  if (loading) return (
    <div className="p-6 flex items-center justify-center min-h-80">
      <div className="text-center">
        <RefreshCw className="w-10 h-10 text-[#ffd879] animate-spin mx-auto mb-4" />
        <p className="text-[#4a4a4a]">Loading analytics…</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="p-6 flex items-center justify-center">
      <Card className="bg-white border-[#e5e5e5] max-w-md">
        <CardContent className="pt-6 text-center">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <p className="text-[#4a4a4a] mb-4">{error}</p>
          <Button
            onClick={() => { isFirstLoad.current = true; setError(null); setLoading(true); fetchStatic().then(() => fetchPeriod(period)).finally(() => { setLoading(false); isFirstLoad.current = false; }); }}
            className="bg-[#1a1a1a] hover:bg-[#2a2a2a] text-white"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Retry
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  const hasData = consolidation && consolidation.total_value > 0;

  const assetAlloc = consolidation?.asset_allocation
    ? Object.entries(consolidation.asset_allocation)
        .map(([key, value], i) => ({
          key, value,
          name: assetClassMap[key]?.label || ALLOC_LABEL_MAP[key] || key,
          color: assetClassMap[key]?.data?.chart_color || CHART_COLORS[i % CHART_COLORS.length],
        }))
        .sort((a, b) => b.value - a.value)
    : [];

  const AC_SUMMARY_NAMES = {
    stocks: 'Stocks', mf: 'Mutual Funds', fd: 'Fixed Deposits',
    gold: 'Gold/Silver', etf: 'ETFs', fo: 'F&O', bonds: 'Debt',
    nps: 'NPS', pms: 'PMS', aif: 'AIF', ppf: 'PPF/EPF', realestate: 'Real Estate',
  };
  const enrichedAssetAlloc = assetAlloc.map(item => {
    const summary = assetClassSummary.find(s => s.name === AC_SUMMARY_NAMES[item.key]);
    return {
      ...item,
      actualValue: summary?.value ?? (consolidation?.total_value * item.value / 100) ?? 0,
      gain_percent: summary?.gain_percent ?? 0,
    };
  });

  const pmCombined = [
    ...pmVC.map(d => ({ ...d, _cat: 'VC' })),
    ...pmPE.map(d => ({ ...d, _cat: 'PE' })),
  ];
  const pmTotalCommitted = pmCombined.reduce((s, d) => s + (d.committed || d.invested || 0), 0);
  const pmTotalInvested  = pmCombined.reduce((s, d) => s + (d.invested || 0), 0);
  const pmTotalValue     = pmCombined.reduce((s, d) => s + (d.current_value || 0), 0);
  const pmMoicItems      = pmCombined.filter(d => d.moic != null);
  const pmAvgMOIC        = pmMoicItems.length ? pmMoicItems.reduce((s, d) => s + d.moic, 0) / pmMoicItems.length : null;
  const pmIrrItems       = pmCombined.filter(d => d.irr != null);
  const pmAvgIRR         = pmIrrItems.length ? pmIrrItems.reduce((s, d) => s + d.irr, 0) / pmIrrItems.length : null;

  const PIPELINE_STAGES = [
    { key: 'sourced', label: 'Sourced' },
    { key: 'initial_review', label: 'Initial Review' },
    { key: 'deep_dive', label: 'Deep Dive' },
    { key: 'due_diligence', label: 'Due Diligence' },
    { key: 'term_sheet', label: 'Term Sheet' },
    { key: 'closing', label: 'Closing' },
    { key: 'closed', label: 'Closed' },
    { key: 'passed', label: 'Passed' },
  ];
  const dealsByStage = PIPELINE_STAGES.reduce((acc, s) => {
    const count = pmDeals.filter(d => d.stage === s.key).length;
    if (count > 0) acc.push({ ...s, count });
    return acc;
  }, []);

  return (
    <div className="p-6" data-testid="analytics-page">
      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#dae4c4] flex items-center justify-center shadow-sm">
            <BarChart3 className="w-5 h-5 text-[#1a1a1a]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[#1a1a1a]">Portfolio Analytics</h1>
            <p className="text-sm text-[#4a4a4a]">Insights, benchmarks &amp; alerts</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {tab === 'overview' && (
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-32 bg-white border-[#e5e5e5] text-[#1a1a1a]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#e5e5e5]">
                {['mtd','qtd','ytd','1y','3y','5y'].map(v => (
                  <SelectItem key={v} value={v} className="text-[#1a1a1a]">{v.toUpperCase()}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            onClick={handleRefresh}
            variant="outline"
            size="sm"
            disabled={refreshing}
            className="border-[#e5e5e5] hover:bg-[#f5f5f5]"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin text-[#ffd879]' : ''}`} />
          </Button>
        </div>
      </div>

      {/* ── Tabs ── */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6 bg-[#f5f5f5] border border-[#e5e5e5] p-1 gap-1">
          <TabsTrigger value="overview" className="data-[state=active]:bg-[#ffd87a] data-[state=active]:text-black">
            <BarChart3 className="w-4 h-4 mr-1.5" /> Overview
          </TabsTrigger>
          <TabsTrigger value="public" className="data-[state=active]:bg-[#ffd87a] data-[state=active]:text-black">
            <Globe className="w-4 h-4 mr-1.5" /> Public Markets
          </TabsTrigger>
          <TabsTrigger value="private" className="data-[state=active]:bg-[#ffd87a] data-[state=active]:text-black">
            <Lock className="w-4 h-4 mr-1.5" /> Private Markets
          </TabsTrigger>
          <TabsTrigger value="insights" className="data-[state=active]:bg-[#ffd87a] data-[state=active]:text-black">
            <Lightbulb className="w-4 h-4 mr-1.5" /> Smart Insights
          </TabsTrigger>
          <TabsTrigger value="alerts" className="data-[state=active]:bg-[#ffd87a] data-[state=active]:text-black">
            <Bell className="w-4 h-4 mr-1.5" /> Alerts
          </TabsTrigger>
        </TabsList>

        {/* ══ OVERVIEW TAB ══════════════════════════════════════════════════════ */}
        <TabsContent value="overview">
          {!hasData ? (
            <div className="bg-white border border-[#e5e5e5] shadow-sm rounded-lg py-20 text-center">
              <Wallet className="w-14 h-14 text-[#4a4a4a] mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-[#2a2a2a] mb-2">No Portfolio Data</h3>
              <p className="text-[#4a4a4a] text-sm">Add holdings to see your portfolio analytics</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Period-refresh indicator */}
              {refreshing && (
                <div className="flex items-center gap-2 text-xs text-[#1a1a1a]">
                  <RefreshCw className="w-3 h-3 animate-spin" /> Updating period data…
                </div>
              )}

              {/* ── Stat Cards ── */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white border border-[#e5e5e5] rounded-lg shadow-sm p-4">
                  <p className="text-xs text-[#4a4a4a] uppercase tracking-widest mb-1">Portfolio FMV</p>
                  <p className="text-xl font-bold text-[#1a1a1a]">{fmt(totalFMV || consolidation.total_value)}</p>
                  <p className="text-xs text-[#4a4a4a] mt-1">Public · Private · Others</p>
                </div>
                <div className="bg-white border border-[#e5e5e5] rounded-lg shadow-sm p-4">
                  <p className="text-xs text-[#4a4a4a] uppercase tracking-widest mb-1">Public Mkts Gain</p>
                  <p className={`text-xl font-bold ${consolidation.total_gain >= 0 ? 'text-emerald-600' : 'text-[#c97a3a]'}`}>{fmt(Math.abs(consolidation.total_gain))}</p>
                  <p className="text-xs text-[#4a4a4a] mt-1">{pct(consolidation.gain_percent)} on invested</p>
                </div>
                <div className="bg-white border border-[#e5e5e5] rounded-lg shadow-sm p-4">
                  <p className="text-xs text-[#4a4a4a] uppercase tracking-widest mb-1">Portfolio XIRR</p>
                  <p className="text-xl font-bold text-[#1a1a1a]">{pct(consolidation?.xirr || 0)}</p>
                  <p className="text-xs text-[#4a4a4a] mt-1">Sharpe: {performance?.sharpe_ratio?.toFixed(2) || '—'}</p>
                </div>
                <div className="bg-white border border-[#e5e5e5] rounded-lg shadow-sm p-4">
                  <p className="text-xs text-[#4a4a4a] uppercase tracking-widest mb-1">Risk Rating</p>
                  <p className="text-xl font-bold text-[#1a1a1a]">{risk?.risk_rating || '—'}</p>
                  <p className="text-xs text-[#4a4a4a] mt-1">Beta: {risk?.beta?.toFixed(2) || '—'}</p>
                </div>
              </div>

              {/* ── Asset Allocation & Sector Exposure ── */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="overflow-hidden border border-[#e5e5e5] shadow-sm rounded-lg flex flex-col">
                  <NavHead icon={PieIcon} title="Asset Allocation" sub="Portfolio distribution by asset class" />
                  <div className="p-5 bg-white flex-1 flex flex-col justify-center">
                    {assetAlloc.length > 0 ? (
                      <div className="flex items-center gap-6">
                        <div className="flex justify-center items-center shrink-0">
                          <DonutChart data={assetAlloc} size={180} />
                        </div>
                        <div className="flex-1 space-y-2 min-w-0">
                          {assetAlloc.slice(0, 7).map((item, i) => (
                            <div key={i} className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: item.color }} />
                                <span className="text-[#4a4a4a] text-sm truncate">{item.name}</span>
                              </div>
                              <span className="text-[#1a1a1a] font-semibold text-sm shrink-0">{item.value.toFixed(1)}%</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-[#4a4a4a] text-sm">No allocation data available</div>
                    )}
                  </div>
                </div>

                <div className="overflow-hidden border border-[#e5e5e5] shadow-sm rounded-lg flex flex-col">
                  <NavHead
                    icon={Building2}
                    title="Sector Exposure"
                    sub={sectorExposure?.top_sector ? `Top: ${sectorExposure.top_sector} (${sectorExposure.top_sector_percent?.toFixed(1)}%)` : 'Equity sector breakdown'}
                  />
                  <div className="p-5 bg-white space-y-3 flex-1 flex flex-col justify-center">
                    {sectorExposure?.sectors?.slice(0, 6).map((s, i) => (
                      <div key={i}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-[#4a4a4a]">{s.name}</span>
                          <span className="text-[#1a1a1a] font-medium">{s.percentage.toFixed(1)}%</span>
                        </div>
                        <div className="h-1.5 bg-[#f5f5f5] rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#ffd879] rounded-full transition-all duration-500"
                            style={{ width: `${s.percentage}%` }}
                          />
                        </div>
                      </div>
                    ))}
                    {sectorExposure?.hhi_index !== undefined && (
                      <div className="pt-3 mt-1 border-t border-[#e5e5e5] flex justify-between text-sm">
                        <span className="text-[#4a4a4a]">HHI Concentration Index</span>
                        <span className={`font-bold ${sectorExposure.hhi_index > 2500 ? 'text-[#c97a3a]' : 'text-emerald-600'}`}>
                          {sectorExposure.hhi_index.toFixed(0)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ── Market Cap & Performance Metrics ── */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="overflow-hidden border border-[#e5e5e5] shadow-sm rounded-lg flex flex-col">
                  <NavHead icon={Layers} title="Market Cap Exposure" sub={`Style: ${marketCapExp?.style || '—'}`} />
                  <div className="p-5 bg-white space-y-4 flex-1 flex flex-col justify-center">
                    {[
                      { label: 'Large Cap', d: marketCapExp?.large_cap },
                      { label: 'Mid Cap',   d: marketCapExp?.mid_cap },
                      { label: 'Small Cap', d: marketCapExp?.small_cap },
                      { label: 'Micro Cap', d: marketCapExp?.micro_cap },
                    ].map(({ label, d }) => (
                      <div key={label}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-[#4a4a4a]">{label}</span>
                          <span className="text-[#1a1a1a] font-medium">{d?.percentage?.toFixed(1) || 0}%</span>
                        </div>
                        <div className="h-1.5 bg-[#f5f5f5] rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#ffd879] rounded-full transition-all duration-500"
                            style={{ width: `${d?.percentage || 0}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="overflow-hidden border border-[#e5e5e5] shadow-sm rounded-lg flex flex-col">
                  <NavHead
                    icon={TrendingUp}
                    title="Performance Metrics"
                    sub="Risk-adjusted indicators"
                    extra={refreshing ? <RefreshCw className="w-3 h-3 animate-spin text-[#ffd87a]" /> : null}
                  />
                  <div className="p-5 bg-white flex-1 flex flex-col justify-center">
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { label: 'Absolute Return', val: pct(performance?.absolute_return || 0), pos: (performance?.absolute_return || 0) >= 0, isRet: true },
                        { label: 'CAGR',            val: pct(performance?.cagr || 0),             pos: (performance?.cagr || 0) >= 0,             isRet: true },
                        { label: 'Sharpe Ratio',    val: performance?.sharpe_ratio?.toFixed(2) || '—',  isRet: false },
                        { label: 'Sortino Ratio',   val: performance?.sortino_ratio?.toFixed(2) || '—', isRet: false },
                        { label: 'Alpha',           val: pct(performance?.alpha || 0),            pos: (performance?.alpha || 0) >= 0,            isRet: true },
                        { label: 'Max Drawdown',    val: `${performance?.max_drawdown?.toFixed(1) || 0}%`, isRisk: true },
                      ].map(({ label, val, pos, isRet, isRisk }) => (
                        <div key={label} className="bg-[#f9f9f9] p-4 rounded-md">
                          <p className="text-xs text-[#4a4a4a] mb-1">{label}</p>
                          <p className={`text-lg font-bold ${isRisk ? 'text-[#c97a3a]' : isRet ? (pos ? 'text-emerald-600' : 'text-[#c97a3a]') : 'text-[#1a1a1a]'}`}>
                            {val}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Benchmark Comparison & Risk ── */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="overflow-hidden border border-[#e5e5e5] shadow-sm rounded-lg flex flex-col">
                  <NavHead
                    icon={Target}
                    title="Benchmark Comparison"
                    sub="Portfolio vs major indices"
                    extra={refreshing ? <RefreshCw className="w-3 h-3 animate-spin text-[#ffd87a]" /> : null}
                  />
                  <div className="p-5 bg-white space-y-3 flex-1 flex flex-col justify-center">
                    {benchmarks.length === 0 ? (
                      <div className="py-8 text-center text-[#4a4a4a] text-sm">No benchmark data available</div>
                    ) : benchmarks.map((bm, i) => (
                      <div key={i} className="bg-[#f9f9f9] p-4 rounded-md">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-[#1a1a1a] font-medium text-sm">{bm.benchmark_name}</span>
                          <Badge className={`text-white text-xs ${bm.excess_return >= 0 ? 'bg-emerald-600' : 'bg-[#c97a3a]'}`}>
                            {bm.excess_return >= 0 ? 'Outperform' : 'Underperform'}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          {[
                            { label: 'Portfolio',  v: bm.portfolio_return },
                            { label: 'Benchmark',  v: bm.benchmark_return },
                            { label: 'Excess',     v: bm.excess_return },
                          ].map(({ label, v }) => (
                            <div key={label}>
                              <p className="text-[#4a4a4a]">{label}</p>
                              <p className={`font-semibold ${v >= 0 ? 'text-emerald-600' : 'text-[#c97a3a]'}`}>{pct(v)}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="overflow-hidden border border-[#e5e5e5] shadow-sm rounded-lg flex flex-col">
                  <NavHead amber icon={Shield} title="Risk Analysis" sub={`Rating: ${risk?.risk_rating || '—'}`} />
                  <div className="p-5 bg-white space-y-3 flex-1 flex flex-col justify-center">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-[#f9f9f9] p-4 rounded-md">
                        <p className="text-xs text-[#4a4a4a] mb-1">Volatility (30D)</p>
                        <p className="text-lg font-bold text-[#1a1a1a]">{risk?.volatility_30d?.toFixed(1) || '—'}%</p>
                      </div>
                      <div className="bg-[#f9f9f9] p-4 rounded-md">
                        <p className="text-xs text-[#4a4a4a] mb-1">Volatility (1Y)</p>
                        <p className="text-lg font-bold text-[#1a1a1a]">{risk?.volatility_1y?.toFixed(1) || '—'}%</p>
                      </div>
                    </div>
                    <div className="bg-[#f9f9f9] p-4 rounded-md">
                      <p className="text-xs text-[#4a4a4a] mb-2">Value at Risk (Daily)</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-xs text-[#4a4a4a]">VaR 95%</p>
                          <p className="text-[#c97a3a] font-semibold text-sm">{fmt(risk?.var_95 || 0)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-[#4a4a4a]">VaR 99%</p>
                          <p className="text-[#c97a3a] font-semibold text-sm">{fmt(risk?.var_99 || 0)}</p>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-[#f9f9f9] p-4 rounded-md">
                        <p className="text-xs text-[#4a4a4a] mb-1">Max Drawdown</p>
                        <p className="text-lg font-bold text-red-500">{risk?.max_drawdown?.toFixed(1) || 0}%</p>
                      </div>
                      <div className="bg-[#f9f9f9] p-4 rounded-md">
                        <p className="text-xs text-[#4a4a4a] mb-1">Current Drawdown</p>
                        <p className="text-lg font-bold text-amber-600">{risk?.current_drawdown?.toFixed(1) || 0}%</p>
                      </div>
                    </div>
                    <div className="space-y-2 pt-2 border-t border-[#e5e5e5] text-sm">
                      <div className="flex justify-between">
                        <span className="text-[#4a4a4a]">Beta (vs {user?.market === 'US' ? 'S&P 500' : 'Nifty'})</span>
                        <span className="font-medium text-[#1a1a1a]">{risk?.beta?.toFixed(2) || '—'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#4a4a4a]">Correlation ({user?.market === 'US' ? 'S&P 500' : 'Nifty'})</span>
                        <span className="font-medium text-[#1a1a1a]">{risk?.correlation_benchmark?.toFixed(2) || '—'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Performance Attribution ── */}
              <div className="overflow-hidden border border-[#e5e5e5] shadow-sm rounded-lg flex flex-col">
                <NavHead
                  icon={Briefcase}
                  title="Performance Attribution"
                  sub="Breakdown of return sources"
                  extra={refreshing ? <RefreshCw className="w-3 h-3 animate-spin text-[#ffd87a]" /> : null}
                />
                <div className="p-5 bg-white">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Effects */}
                    <div className="space-y-2">
                      <h4 className="text-[#2a2a2a] font-semibold text-sm mb-3">Attribution Effects</h4>
                      {[
                        ['Total Return',       attribution?.total_return],
                        ['Allocation Effect',  attribution?.allocation_effect],
                        ['Selection Effect',   attribution?.selection_effect],
                        ['Interaction Effect', attribution?.interaction_effect],
                      ].map(([label, v]) => (
                        <div key={label} className="flex justify-between items-center bg-[#f9f9f9] px-4 py-3 rounded-md">
                          <span className="text-[#4a4a4a] text-sm">{label}</span>
                          <span className={`font-bold text-sm ${(v || 0) >= 0 ? 'text-emerald-600' : 'text-[#c97a3a]'}`}>{pct(v || 0)}</span>
                        </div>
                      ))}
                    </div>
                    {/* Top Gainers */}
                    <div className="space-y-2">
                      <h4 className="text-[#2a2a2a] font-semibold text-sm mb-3 flex items-center gap-1">
                        <ArrowUpRight className="w-4 h-4 text-emerald-500" /> Top Gainers
                      </h4>
                      {attribution?.top_gainers?.slice(0, 5).map((item, i) => (
                        <div key={i} className="flex justify-between items-center bg-[#f9f9f9] px-4 py-3 rounded-md">
                          <div>
                            <p className="text-[#1a1a1a] font-medium text-sm">{item.symbol}</p>
                            <p className="text-[#4a4a4a] text-xs">{fmt(item.gain)}</p>
                          </div>
                          <span className="text-emerald-600 font-bold text-sm">+{item.contribution.toFixed(2)}%</span>
                        </div>
                      ))}
                      {!attribution?.top_gainers?.length && (
                        <p className="text-[#4a4a4a] text-sm text-center py-6">No gainers</p>
                      )}
                    </div>
                    {/* Top Losers */}
                    <div className="space-y-2">
                      <h4 className="text-[#2a2a2a] font-semibold text-sm mb-3 flex items-center gap-1">
                        <ArrowDownRight className="w-4 h-4 text-red-500" /> Top Losers
                      </h4>
                      {attribution?.top_losers?.slice(0, 5).map((item, i) => (
                        <div key={i} className="flex justify-between items-center bg-[#f9f9f9] px-4 py-3 rounded-md">
                          <div>
                            <p className="text-[#1a1a1a] font-medium text-sm">{item.symbol}</p>
                            <p className="text-[#4a4a4a] text-xs">{fmt(Math.abs(item.gain))}</p>
                          </div>
                          <span className="text-red-500 font-bold text-sm">{item.contribution.toFixed(2)}%</span>
                        </div>
                      ))}
                      {!attribution?.top_losers?.length && (
                        <p className="text-[#4a4a4a] text-sm text-center py-6">No losers</p>
                      )}
                    </div>
                  </div>

                  {/* Asset Class Attribution */}
                  {attribution?.asset_class_attribution && Object.keys(attribution.asset_class_attribution).length > 0 && (
                    <div className="mt-6 pt-5 border-t border-[#e5e5e5]">
                      <h4 className="text-[#2a2a2a] font-semibold text-sm mb-4">Attribution by Asset Class</h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {Object.entries(attribution.asset_class_attribution).map(([ac, data], i) => (
                          <div key={i} className="bg-[#f9f9f9] p-4 rounded-md">
                            <p className="text-[#4a4a4a] text-xs mb-2 font-medium">{ac}</p>
                            <div className="flex justify-between text-xs mb-1">
                              <span className="text-[#4a4a4a]">Weight</span>
                              <span className="text-[#1a1a1a] font-medium">{data.weight.toFixed(1)}%</span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-[#4a4a4a]">Contribution</span>
                              <span className={`font-medium ${data.contribution >= 0 ? 'text-emerald-600' : 'text-[#c97a3a]'}`}>
                                {pct(data.contribution)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* ── Portfolio Breakdown Table ── */}
              {consolidation?.portfolio_breakdown?.length > 0 && (
                <div className="overflow-hidden border border-[#e5e5e5] shadow-sm rounded-lg flex flex-col">
                  <div className="bg-[#dae4c4] px-5 py-3">
                    <h3 className="text-sm font-semibold text-[#1a1a1a] flex items-center gap-2">
                      <CircleDollarSign className="w-4 h-4" /> Portfolio Breakdown
                    </h3>
                    <p className="text-xs text-[#4a4a4a]">
                      {consolidation.portfolios_count} portfolios · {consolidation.holdings_count} holdings
                    </p>
                  </div>
                  <table className="w-full">
                    <thead>
                      <tr className="bg-[#f9f9f9] border-b border-[#e5e5e5]">
                        {['Portfolio','Value','Invested','Gain','Return','Holdings'].map((h, i) => (
                          <th key={h} className={`py-3 px-4 text-xs font-semibold text-[#4a4a4a] uppercase tracking-wider ${i === 0 ? 'text-left' : 'text-right'}`}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#e5e5e5]">
                      {consolidation.portfolio_breakdown.map((p, i) => (
                        <tr key={i} className={`hover:bg-[#f9f9f9] transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-[#fafafa]'}`}>
                          <td className="py-3 px-4 font-semibold text-[#1a1a1a]">{p.name}</td>
                          <td className="py-3 px-4 text-right text-[#1a1a1a]">{fmt(p.value)}</td>
                          <td className="py-3 px-4 text-right text-[#4a4a4a]">{fmt(p.invested)}</td>
                          <td className={`py-3 px-4 text-right font-medium ${p.gain >= 0 ? 'text-emerald-600' : 'text-[#c97a3a]'}`}>{fmt(Math.abs(p.gain))}</td>
                          <td className={`py-3 px-4 text-right font-medium ${p.gain_percent >= 0 ? 'text-emerald-600' : 'text-[#c97a3a]'}`}>{pct(p.gain_percent)}</td>
                          <td className="py-3 px-4 text-right text-[#4a4a4a]">{p.holdings_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* ── NAV History ── */}
              {/* {navData && (
                <div className="overflow-hidden border border-[#e5e5e5] shadow-sm rounded-lg flex flex-col">
                  <NavHead
                    icon={LineIcon}
                    title="NAV History"
                    sub={`${navData.portfolio_name} · CAGR since inception: ${pct(navData.cagr_since_inception)}`}
                  />
                  <div className="p-5 bg-white">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                      {[
                        { label: 'Current NAV', val: navData.current_nav?.toFixed(4) },
                        { label: 'Day Change',  val: pct(navData.day_change_percent), color: navData.day_change >= 0 ? 'text-emerald-600' : 'text-[#c97a3a]' },
                        { label: 'Total Units', val: navData.total_units?.toFixed(2) },
                        { label: 'AUM',         val: fmt(navData.aum) },
                      ].map(({ label, val, color }) => (
                        <div key={label} className="bg-[#f9f9f9] p-4 rounded-md">
                          <p className="text-xs text-[#4a4a4a] mb-1">{label}</p>
                          <p className={`text-lg font-bold ${color || 'text-[#1a1a1a]'}`}>{val}</p>
                        </div>
                      ))}
                    </div>
                    {navData.history?.length > 0 ? (
                      <div className="h-56 w-full">
                        <NavLineChart data={navData.history.slice().reverse()} height={224} width={860} />
                      </div>
                    ) : (
                      <div className="h-36 flex items-center justify-center bg-[#f9f9f9]">
                        <p className="text-[#4a4a4a] text-sm">NAV history will appear here over time</p>
                      </div>
                    )}
                    <div className="mt-4 pt-4 border-t border-[#e5e5e5] grid grid-cols-2 gap-4 text-sm text-[#4a4a4a]">
                      <div>Inception: <span className="text-[#1a1a1a] font-medium">{navData.inception_date?.split('T')[0]}</span></div>
                      <div>Inception NAV: <span className="text-[#1a1a1a] font-medium">{navData.inception_nav?.toFixed(4)}</span></div>
                    </div>
                  </div>
                </div>
              )} */}
            </div>
          )}
        </TabsContent>

        {/* ══ SMART INSIGHTS TAB ════════════════════════════════════════════════ */}
        <TabsContent value="insights" className="-mx-6 -mb-6">
          <SmartInsightsPage />
        </TabsContent>

        {/* ══ ALERTS TAB ════════════════════════════════════════════════════════ */}
        <TabsContent value="alerts" className="-mx-6 -mb-6">
          <AlertsPage />
        </TabsContent>

        {/* ══ PUBLIC MARKETS TAB ═══════════════════════════════════════════════ */}
        <TabsContent value="public">
          {!hasData ? (
            <div className="bg-white border border-[#e5e5e5] py-20 text-center">
              <Wallet className="w-14 h-14 text-[#4a4a4a] mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-[#2a2a2a] mb-2">No Portfolio Data</h3>
              <p className="text-[#4a4a4a] text-sm">Add public market holdings to see analytics</p>
            </div>
          ) : (
            <div className="space-y-6">
              {refreshing && (
                <div className="flex items-center gap-2 text-xs text-[#4a4a4a]">
                  <RefreshCw className="w-3 h-3 animate-spin" /> Refreshing…
                </div>
              )}

              {/* ── Stat strip ── */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white border border-[#e5e5e5] rounded-lg shadow-sm p-5">
                  <p className="text-xs text-[#4a4a4a] uppercase tracking-widest mb-2">Public Markets FMV</p>
                  <p className="text-2xl font-bold text-[#1a1a1a] leading-none">{fmt(consolidation.total_value)}</p>
                  <p className={`text-xs mt-2 ${consolidation.day_change >= 0 ? 'text-emerald-600' : 'text-[#c97a3a]'}`}>
                    {consolidation.day_change >= 0 ? '▲' : '▼'} {fmt(Math.abs(consolidation.day_change))} today
                  </p>
                </div>
                <div className="bg-white border border-[#e5e5e5] rounded-lg shadow-sm p-5">
                  <p className="text-xs text-[#4a4a4a] uppercase tracking-widest mb-2">Total Gain / Loss</p>
                  <p className={`text-2xl font-bold leading-none ${consolidation.total_gain >= 0 ? 'text-emerald-600' : 'text-[#c97a3a]'}`}>{fmt(Math.abs(consolidation.total_gain))}</p>
                  <p className="text-xs text-[#4a4a4a] mt-2">{pct(consolidation.gain_percent)} overall</p>
                </div>
                <div className="bg-white border border-[#e5e5e5] rounded-lg shadow-sm p-5">
                  <p className="text-xs text-[#4a4a4a] uppercase tracking-widest mb-2">Portfolio XIRR</p>
                  <p className="text-2xl font-bold text-[#1a1a1a] leading-none">{pct(consolidation?.xirr || 0)}</p>
                  <p className="text-xs text-[#4a4a4a] mt-2">Sharpe: {performance?.sharpe_ratio?.toFixed(2) || '—'}</p>
                </div>
                <div className="bg-white border border-[#e5e5e5] rounded-lg shadow-sm p-5">
                  <p className="text-xs text-[#4a4a4a] uppercase tracking-widest mb-2">Risk Rating</p>
                  <p className="text-2xl font-bold text-[#1a1a1a] leading-none">{risk?.risk_rating || '—'}</p>
                  <p className="text-xs text-[#4a4a4a] mt-2">Beta: {risk?.beta?.toFixed(2) || '—'}</p>
                </div>
              </div>

              {/* ── Asset Allocation + Sector Exposure ── */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="overflow-hidden border border-[#e5e5e5] shadow-sm bg-white rounded-lg flex flex-col">
                  <NavHead icon={PieIcon} title="Asset Allocation" sub="Portfolio distribution by asset class" />
                  <div className="p-5 flex-1 flex flex-col justify-center">
                    {enrichedAssetAlloc.length > 0 ? (
                      <div className="flex gap-6 items-center">
                        <div className="flex justify-center items-center shrink-0">
                          <DonutChart data={enrichedAssetAlloc} size={170} />
                        </div>
                        <div className="flex-1 space-y-2.5 min-w-0">
                          {enrichedAssetAlloc.slice(0, 7).map((item, i) => (
                            <div key={i} className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: item.color }} />
                                <span className="text-[#4a4a4a] text-xs truncate">{item.name}</span>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <span className={`text-xs font-medium ${item.gain_percent >= 0 ? 'text-emerald-600' : 'text-[#c97a3a]'}`}>
                                  {item.gain_percent >= 0 ? '+' : ''}{item.gain_percent.toFixed(1)}%
                                </span>
                                <span className="text-[#1a1a1a] font-bold text-xs w-9 text-right">{item.value.toFixed(1)}%</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-10 text-[#4a4a4a] text-sm">No allocation data</div>
                    )}
                  </div>
                </div>

                <div className="overflow-hidden border border-[#e5e5e5] shadow-sm bg-white rounded-lg flex flex-col">
                  <NavHead
                    icon={Building2}
                    title="Sector Exposure"
                    sub={sectorExposure?.top_sector ? `Top: ${sectorExposure.top_sector} · ${sectorExposure.top_sector_percent?.toFixed(1)}%` : 'Equity sector breakdown'}
                  />
                  <div className="p-5 space-y-3 flex-1 flex flex-col justify-center">
                    {sectorExposure?.sectors?.slice(0, 8).map((s, i) => (
                      <div key={i}>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-[#4a4a4a]">{s.name}</span>
                          <span className="text-[#1a1a1a] font-semibold">{s.percentage.toFixed(1)}%</span>
                        </div>
                        <div className="h-1.5 bg-[#f5f5f5] rounded-full overflow-hidden">
                          <div className="h-full bg-[#ffd879] rounded-full transition-all duration-500" style={{ width: `${s.percentage}%` }} />
                        </div>
                      </div>
                    ))}
                    {!sectorExposure?.sectors?.length && (
                      <div className="text-center py-10 text-[#4a4a4a] text-sm">No sector data available</div>
                    )}
                    {sectorExposure?.hhi_index !== undefined && (
                      <div className="pt-3 border-t border-[#e5e5e5] flex justify-between text-xs">
                        <span className="text-[#4a4a4a]">HHI Concentration Index</span>
                        <span className={`font-bold ${sectorExposure.hhi_index > 2500 ? 'text-[#c97a3a]' : 'text-emerald-600'}`}>
                          {sectorExposure.hhi_index.toFixed(0)} {sectorExposure.hhi_index > 2500 ? '· Concentrated' : '· Diversified'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ── Market Cap + Risk Analysis ── */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="overflow-hidden border border-[#e5e5e5] shadow-sm bg-white rounded-lg flex flex-col">
                  <NavHead icon={Layers} title="Market Cap Exposure" sub={`Portfolio style: ${marketCapExp?.style || '—'}`} />
                  <div className="p-5 space-y-4 flex-1 flex flex-col justify-center">
                    {[
                      { label: 'Large Cap', d: marketCapExp?.large_cap, color: '#ffd879' },
                      { label: 'Mid Cap',   d: marketCapExp?.mid_cap,   color: '#d9e2c3' },
                      { label: 'Small Cap', d: marketCapExp?.small_cap, color: '#a8a8a8' },
                      { label: 'Micro Cap', d: marketCapExp?.micro_cap, color: '#c97a3a' },
                    ].map(({ label, d, color }) => (
                      <div key={label}>
                        <div className="flex justify-between text-xs mb-1.5">
                          <span className="text-[#4a4a4a]">{label}</span>
                          <div className="flex items-center gap-3">
                            <span className="text-[#4a4a4a]">{fmt(d?.value || 0)}</span>
                            <span className="text-[#1a1a1a] font-semibold w-10 text-right">{d?.percentage?.toFixed(1) || 0}%</span>
                          </div>
                        </div>
                        <div className="h-2 bg-[#f5f5f5] rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${d?.percentage || 0}%`, backgroundColor: color }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="overflow-hidden border border-[#e5e5e5] shadow-sm bg-white rounded-lg flex flex-col">
                  <NavHead amber icon={Shield} title="Risk Analysis" sub={`Rating: ${risk?.risk_rating || '—'} · Beta: ${risk?.beta?.toFixed(2) || '—'}`} />
                  <div className="p-5 flex-1 flex flex-col justify-center">
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div className="bg-[#f9f9f9] p-4 rounded-md">
                        <p className="text-xs text-[#4a4a4a] mb-1">Volatility 30D</p>
                        <p className="text-lg font-bold text-[#1a1a1a]">{risk?.volatility_30d?.toFixed(1) || '—'}%</p>
                      </div>
                      <div className="bg-[#f9f9f9] p-4 rounded-md">
                        <p className="text-xs text-[#4a4a4a] mb-1">Volatility 1Y</p>
                        <p className="text-lg font-bold text-[#1a1a1a]">{risk?.volatility_1y?.toFixed(1) || '—'}%</p>
                      </div>
                      <div className="bg-[#f9f9f9] p-4 rounded-md">
                        <p className="text-xs text-[#4a4a4a] mb-1">Max Drawdown</p>
                        <p className="text-lg font-bold text-[#c97a3a]">{risk?.max_drawdown?.toFixed(1) || 0}%</p>
                      </div>
                      <div className="bg-[#f9f9f9] p-4 rounded-md">
                        <p className="text-xs text-[#4a4a4a] mb-1">Current Drawdown</p>
                        <p className="text-lg font-bold text-amber-600">{risk?.current_drawdown?.toFixed(1) || 0}%</p>
                      </div>
                    </div>
                    <div className="bg-[#f9f9f9] p-3 mb-3">
                      <p className="text-xs text-[#4a4a4a] mb-2">Value at Risk (Daily)</p>
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        <div>
                          <p className="text-[#4a4a4a]">VaR 95%</p>
                          <p className="text-[#c97a3a] font-semibold text-sm mt-0.5">{fmt(risk?.var_95 || 0)}</p>
                        </div>
                        <div>
                          <p className="text-[#4a4a4a]">VaR 99%</p>
                          <p className="text-[#c97a3a] font-semibold text-sm mt-0.5">{fmt(risk?.var_99 || 0)}</p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2 text-xs">
                      <div className="flex justify-between py-1 border-b border-[#f5f5f5]">
                        <span className="text-[#4a4a4a]">Correlation (Nifty/S&P)</span>
                        <span className="font-medium text-[#1a1a1a]">{risk?.correlation_benchmark?.toFixed(2) || '—'}</span>
                      </div>
                      <div className="flex justify-between py-1">
                        <span className="text-[#4a4a4a]">Sortino Ratio</span>
                        <span className="font-medium text-[#1a1a1a]">{performance?.sortino_ratio?.toFixed(2) || '—'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Performance Metrics full-width ── */}
              <div className="overflow-hidden border border-[#e5e5e5] shadow-sm bg-white rounded-lg flex flex-col">
                <NavHead icon={TrendingUp} title="Performance Metrics" sub="Risk-adjusted return indicators"
                  extra={refreshing ? <RefreshCw className="w-3 h-3 animate-spin text-[#ffd87a]" /> : null} />
                <div className="p-5">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    {[
                      { label: 'Absolute Return', val: pct(performance?.absolute_return || 0), pos: (performance?.absolute_return || 0) >= 0, isRet: true },
                      { label: 'CAGR',            val: pct(performance?.cagr || 0),             pos: (performance?.cagr || 0) >= 0,             isRet: true },
                      { label: 'Alpha',           val: pct(performance?.alpha || 0),            pos: (performance?.alpha || 0) >= 0,            isRet: true },
                      { label: 'Sharpe Ratio',    val: performance?.sharpe_ratio?.toFixed(2) || '—' },
                      { label: 'Sortino Ratio',   val: performance?.sortino_ratio?.toFixed(2) || '—' },
                      { label: 'Max Drawdown',    val: `${performance?.max_drawdown?.toFixed(1) || 0}%`, isRisk: true },
                    ].map(({ label, val, pos, isRet, isRisk }) => (
                      <div key={label} className="bg-[#f9f9f9] p-4 rounded-md text-center">
                        <p className="text-xs text-[#4a4a4a] mb-2">{label}</p>
                        <p className={`text-xl font-bold ${isRisk ? 'text-[#c97a3a]' : isRet ? (pos ? 'text-emerald-600' : 'text-[#c97a3a]') : 'text-[#1a1a1a]'}`}>{val}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* ── Benchmark Comparison ── */}
              <div className="overflow-hidden border border-[#e5e5e5] shadow-sm bg-white rounded-lg flex flex-col">
                <NavHead icon={Target} title="Benchmark Comparison" sub="Portfolio vs major indices"
                  extra={refreshing ? <RefreshCw className="w-3 h-3 animate-spin text-[#ffd87a]" /> : null} />
                <div className="p-5">
                  {benchmarks.length === 0 ? (
                    <div className="py-10 text-center text-[#4a4a4a] text-sm">No benchmark data available</div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {benchmarks.map((bm, i) => (
                        <div key={i} className="bg-[#f9f9f9] p-4 rounded-md">
                          <div className="flex justify-between items-start mb-3">
                            <span className="text-[#1a1a1a] font-semibold text-sm leading-tight">{bm.benchmark_name}</span>
                            <Badge className={`text-white text-xs border-0 shrink-0 ml-2 ${bm.excess_return >= 0 ? 'bg-emerald-600' : 'bg-[#c97a3a]'}`}>
                              {bm.excess_return >= 0 ? '↑ Alpha' : '↓ Lagging'}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            {[
                              { label: 'Portfolio', v: bm.portfolio_return },
                              { label: 'Benchmark', v: bm.benchmark_return },
                              { label: 'Excess',    v: bm.excess_return },
                            ].map(({ label, v }) => (
                              <div key={label} className="text-center bg-white p-2 rounded-md">
                                <p className="text-[10px] text-[#4a4a4a] uppercase tracking-wide mb-1">{label}</p>
                                <p className={`text-sm font-bold ${v >= 0 ? 'text-emerald-600' : 'text-[#c97a3a]'}`}>{pct(v)}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* ── Attribution: Effects + Gainers + Losers ── */}
              <div className="overflow-hidden border border-[#e5e5e5] shadow-sm bg-white rounded-lg flex flex-col">
                <NavHead icon={Briefcase} title="Performance Attribution" sub="Return decomposition by stock and effect"
                  extra={refreshing ? <RefreshCw className="w-3 h-3 animate-spin text-[#ffd87a]" /> : null} />
                <div className="p-5">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Attribution effects */}
                    <div>
                      <p className="text-xs font-semibold text-[#4a4a4a] uppercase tracking-wider mb-3">Attribution Effects</p>
                      <div className="space-y-2">
                        {[
                          ['Total Return',       attribution?.total_return],
                          ['Allocation Effect',  attribution?.allocation_effect],
                          ['Selection Effect',   attribution?.selection_effect],
                          ['Interaction Effect', attribution?.interaction_effect],
                        ].map(([label, v]) => (
                          <div key={label} className="flex justify-between items-center bg-[#f9f9f9] px-4 py-3 rounded-md">
                            <span className="text-[#4a4a4a] text-xs">{label}</span>
                            <span className={`font-bold text-sm ${(v || 0) >= 0 ? 'text-emerald-600' : 'text-[#c97a3a]'}`}>{pct(v || 0)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    {/* Top gainers */}
                    <div>
                      <p className="text-xs font-semibold text-[#4a4a4a] uppercase tracking-wider mb-3 flex items-center gap-1">
                        <ArrowUpRight className="w-3.5 h-3.5 text-emerald-500" /> Top Gainers
                      </p>
                      <div className="space-y-2">
                        {attribution?.top_gainers?.slice(0, 5).map((item, i) => (
                          <div key={i} className="flex justify-between items-center bg-[#f9f9f9] px-4 py-3 rounded-md">
                            <div>
                              <p className="text-[#1a1a1a] font-semibold text-sm">{item.symbol}</p>
                              <p className="text-[#4a4a4a] text-xs">{fmt(item.gain)}</p>
                            </div>
                            <span className="text-emerald-600 font-bold text-sm">+{item.contribution.toFixed(2)}%</span>
                          </div>
                        ))}
                        {!attribution?.top_gainers?.length && <p className="text-[#4a4a4a] text-xs text-center py-6">No data</p>}
                      </div>
                    </div>
                    {/* Top losers */}
                    <div>
                      <p className="text-xs font-semibold text-[#4a4a4a] uppercase tracking-wider mb-3 flex items-center gap-1">
                        <ArrowDownRight className="w-3.5 h-3.5 text-[#c97a3a]" /> Top Losers
                      </p>
                      <div className="space-y-2">
                        {attribution?.top_losers?.slice(0, 5).map((item, i) => (
                          <div key={i} className="flex justify-between items-center bg-[#f9f9f9] px-4 py-3 rounded-md">
                            <div>
                              <p className="text-[#1a1a1a] font-semibold text-sm">{item.symbol}</p>
                              <p className="text-[#4a4a4a] text-xs">{fmt(Math.abs(item.gain))}</p>
                            </div>
                            <span className="text-[#c97a3a] font-bold text-sm">{item.contribution.toFixed(2)}%</span>
                          </div>
                        ))}
                        {!attribution?.top_losers?.length && <p className="text-[#4a4a4a] text-xs text-center py-6">No data</p>}
                      </div>
                    </div>
                  </div>

                  {/* Attribution by asset class */}
                  {attribution?.asset_class_attribution && Object.keys(attribution.asset_class_attribution).length > 0 && (
                    <div className="mt-6 pt-5 border-t border-[#e5e5e5]">
                      <p className="text-xs font-semibold text-[#4a4a4a] uppercase tracking-wider mb-4">Attribution by Asset Class</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {Object.entries(attribution.asset_class_attribution).map(([ac, data], i) => (
                          <div key={i} className="bg-[#f9f9f9] p-4 rounded-md">
                            <p className="text-[#4a4a4a] text-xs mb-3 font-medium uppercase tracking-wide">{ac}</p>
                            <div className="flex justify-between text-xs mb-1">
                              <span className="text-[#4a4a4a]">Weight</span>
                              <span className="text-[#1a1a1a] font-semibold">{data.weight.toFixed(1)}%</span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-[#4a4a4a]">Contribution</span>
                              <span className={`font-semibold ${data.contribution >= 0 ? 'text-emerald-600' : 'text-[#c97a3a]'}`}>{pct(data.contribution)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        {/* ══ PRIVATE MARKETS TAB ══════════════════════════════════════════════ */}
        <TabsContent value="private">
          <div className="space-y-6">

            {/* ── Hero KPI strip ── */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white border border-[#e5e5e5] rounded-lg shadow-sm p-5">
                <p className="text-xs text-[#4a4a4a] uppercase tracking-widest mb-2">Portfolio Value</p>
                <p className="text-2xl font-bold text-[#1a1a1a] leading-none">{fmt(pmTotalValue)}</p>
                <p className="text-xs text-[#4a4a4a] mt-2">{pmCombined.length} funds · marked to NAV</p>
              </div>
              <div className="bg-white border border-[#e5e5e5] rounded-lg shadow-sm p-5">
                <p className="text-xs text-[#4a4a4a] uppercase tracking-widest mb-2">Committed → Deployed</p>
                <p className="text-2xl font-bold text-[#1a1a1a] leading-none">{fmt(pmTotalInvested)}</p>
                <div className="mt-2">
                  <div className="flex justify-between text-xs text-[#4a4a4a] mb-1">
                    <span>{pmTotalCommitted > 0 ? ((pmTotalInvested / pmTotalCommitted) * 100).toFixed(0) : 0}% utilised</span>
                    <span>{fmt(pmTotalCommitted)}</span>
                  </div>
                  <div className="h-1 bg-[#e5e5e5] rounded-full overflow-hidden">
                    <div className="h-full bg-[#ffd87a] rounded-full transition-all duration-700"
                      style={{ width: `${pmTotalCommitted > 0 ? Math.min((pmTotalInvested / pmTotalCommitted) * 100, 100) : 0}%` }} />
                  </div>
                </div>
              </div>
              <div className="bg-white border border-[#e5e5e5] rounded-lg shadow-sm p-5">
                <p className="text-xs text-[#4a4a4a] uppercase tracking-widest mb-2">Blended MOIC</p>
                <p className="text-2xl font-bold text-[#1a1a1a] leading-none">{pmAvgMOIC ? `${pmAvgMOIC.toFixed(2)}x` : '—'}</p>
                <p className="text-xs text-[#4a4a4a] mt-2">{pmMoicItems.length} funds reporting</p>
              </div>
              <div className="bg-white border border-[#e5e5e5] rounded-lg shadow-sm p-5">
                <p className="text-xs text-[#4a4a4a] uppercase tracking-widest mb-2">Unrealised P&amp;L</p>
                <p className={`text-2xl font-bold leading-none ${pmTotalValue >= pmTotalInvested ? 'text-emerald-600' : 'text-[#c97a3a]'}`}>{fmt(Math.abs(pmTotalValue - pmTotalInvested))}</p>
                <p className="text-xs text-[#4a4a4a] mt-2">
                  {pmAvgIRR ? `Avg IRR ${pmAvgIRR.toFixed(1)}%` : pmTotalInvested > 0 ? `${((pmTotalValue - pmTotalInvested) / pmTotalInvested * 100).toFixed(1)}% on cost` : '—'}
                </p>
              </div>
            </div>

            {/* ── Allocation donut + MOIC bar chart ── */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

              {/* Strategy allocation donut */}
              <div className="overflow-hidden border border-[#e5e5e5] shadow-sm bg-white rounded-lg flex flex-col">
                <NavHead icon={PieIcon} title="Strategy Mix" sub="AUM split by fund type" />
                <div className="p-5">
                  {pmCombined.length > 0 ? (() => {
                    const vcVal = pmVC.reduce((s, d) => s + (d.current_value || 0), 0);
                    const peVal = pmPE.reduce((s, d) => s + (d.current_value || 0), 0);
                    const slices = [
                      vcVal > 0 && { name: 'Venture Capital', value: vcVal, color: '#ffd879' },
                      peVal > 0 && { name: 'Private Equity',  value: peVal, color: '#c97a3a' },
                    ].filter(Boolean);
                    return (
                      <>
                        <DonutChart data={slices} size={150} />
                        <div className="mt-5 space-y-3">
                          {slices.map((s, i) => (
                            <div key={i} className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                                <span className="text-xs text-[#4a4a4a]">{s.name}</span>
                              </div>
                              <div className="text-right">
                                <span className="text-xs font-bold text-[#1a1a1a]">{fmt(s.value)}</span>
                                <span className="text-xs text-[#4a4a4a] ml-1">· {pmTotalValue > 0 ? ((s.value / pmTotalValue) * 100).toFixed(0) : 0}%</span>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="mt-5 pt-4 border-t border-[#e5e5e5] grid grid-cols-2 gap-3">
                          <div className="bg-[#f9f9f9] p-3 text-center">
                            <p className="text-xs text-[#4a4a4a]">VC Funds</p>
                            <p className="text-xl font-bold text-[#1a1a1a]">{pmVC.length}</p>
                          </div>
                          <div className="bg-[#f9f9f9] p-3 text-center">
                            <p className="text-xs text-[#4a4a4a]">PE Funds</p>
                            <p className="text-xl font-bold text-[#1a1a1a]">{pmPE.length}</p>
                          </div>
                        </div>
                      </>
                    );
                  })() : (
                    <div className="text-center py-12 text-[#4a4a4a] text-sm">No fund data yet</div>
                  )}
                </div>
              </div>

              {/* MOIC comparison bar chart */}
              <div className="col-span-2 overflow-hidden border border-[#e5e5e5] shadow-sm bg-white rounded-lg flex flex-col">
                <NavHead icon={TrendingUp} title="Fund Performance" sub="MOIC comparison across portfolio (sorted by multiple)" />
                <div className="p-5">
                  {pmCombined.filter(d => d.moic != null).length > 0 ? (() => {
                    const sorted = [...pmCombined].filter(d => d.moic != null).sort((a, b) => b.moic - a.moic);
                    const maxMoic = Math.max(...sorted.map(d => d.moic), 1);
                    return (
                      <>
                        <div className="space-y-3">
                          {sorted.map((d, i) => (
                            <div key={i} className="flex items-center gap-3">
                              <div className="w-32 shrink-0 text-right">
                                <p className="text-xs font-semibold text-[#1a1a1a] truncate">{d.name}</p>
                                <p className="text-[10px] text-[#4a4a4a]">{d._cat}{d.vintage ? ` · ${d.vintage}` : ''}</p>
                              </div>
                              <div className="flex-1 bg-[#f5f5f5] h-8 relative overflow-hidden">
                                <div
                                  className="absolute inset-y-0 left-0 transition-all duration-700"
                                  style={{
                                    width: `${(d.moic / (maxMoic * 1.05)) * 100}%`,
                                    backgroundColor: d._cat === 'VC' ? '#ffd879' : '#c97a3a',
                                  }}
                                />
                                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-xs font-bold text-[#1a1a1a] z-10">
                                  {d.moic}x
                                </span>
                              </div>
                              <div className="w-20 shrink-0 text-right">
                                {d.irr != null && <p className="text-xs font-semibold text-emerald-600">{d.irr}% IRR</p>}
                                {d.status && <p className="text-[10px] text-[#4a4a4a]">{d.status}</p>}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="mt-4 pt-3 border-t border-[#f5f5f5] flex items-center gap-5 text-xs text-[#4a4a4a]">
                          <span className="flex items-center gap-1.5"><span className="w-3 h-3 inline-block bg-[#ffd879]" />Venture Capital</span>
                          <span className="flex items-center gap-1.5"><span className="w-3 h-3 inline-block bg-[#c97a3a]" />Private Equity</span>
                        </div>
                      </>
                    );
                  })() : (
                    <div className="text-center py-12 text-[#4a4a4a] text-sm">No MOIC data available — add fund performance data to see comparisons</div>
                  )}
                </div>
              </div>
            </div>

            {/* ── Fund cards grid ── */}
            <div className="overflow-hidden border border-[#e5e5e5] shadow-sm bg-white rounded-lg flex flex-col">
              <NavHead icon={Building2} title="PE / VC Holdings"
                sub={`${pmCombined.length} positions · ${pmCombined.filter(d => ['Active','Investing'].includes(d.status)).length} active · ${pmCombined.filter(d => d.status === 'Exited').length} exited`} />
              {pmCombined.length === 0 ? (
                <div className="py-16 text-center">
                  <Building2 className="w-12 h-12 text-[#e5e5e5] mx-auto mb-4" />
                  <p className="text-sm text-[#4a4a4a]">No PE / VC positions yet</p>
                </div>
              ) : (
                <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {pmCombined.map((d, i) => {
                    const gain = (d.current_value || 0) - (d.invested || 0);
                    const utilPct = d.committed ? Math.min(((d.invested || 0) / d.committed) * 100, 100) : 100;
                    const isUp = gain >= 0;
                    return (
                      <div key={i} className="border border-[#e5e5e5] hover:border-[#c0c0c0] transition-colors bg-white">
                        {/* Card header */}
                        <div className={`px-4 py-3 flex items-start justify-between ${d._cat === 'VC' ? 'bg-[#ffd879]/10 border-b border-[#ffd879]/20' : 'bg-[#1a1a1a]/5 border-b border-[#e5e5e5]'}`}>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className={`px-1.5 py-0.5 text-[10px] font-bold shrink-0 ${d._cat === 'VC' ? 'bg-[#ffd879] text-[#1a1a1a]' : 'bg-[#1a1a1a] text-[#ffd879]'}`}>{d._cat}</span>
                              {d.vintage && <span className="text-[10px] text-[#4a4a4a]">Vintage {d.vintage}</span>}
                            </div>
                            <p className="font-bold text-[#1a1a1a] text-sm leading-tight">{d.name}</p>
                            <p className="text-xs text-[#4a4a4a] mt-0.5 truncate">{[d.manager || d.sector, d.strategy || d.stage].filter(Boolean).join(' · ') || 'Private fund'}</p>
                          </div>
                          {d.status && (
                            <Badge className={`text-xs border-0 shrink-0 ml-3 ${
                              ['Active','Investing'].includes(d.status) ? 'bg-emerald-100 text-emerald-700' :
                              ['Harvesting','Pre-IPO'].includes(d.status) ? 'bg-[#ffd879]/40 text-[#1a1a1a]' :
                              d.status === 'Exited' ? 'bg-[#f5f5f5] text-[#4a4a4a]' :
                              'bg-amber-100 text-amber-700'
                            }`}>{d.status}</Badge>
                          )}
                        </div>

                        {/* Capital utilisation bar */}
                        <div className="px-4 pt-3">
                          <div className="flex justify-between text-[10px] text-[#4a4a4a] mb-1">
                            <span>Capital deployed</span>
                            <span>{fmt(d.invested || 0)} {d.committed ? `of ${fmt(d.committed)}` : ''}</span>
                          </div>
                          <div className="h-1.5 bg-[#f5f5f5] rounded-full overflow-hidden">
                            <div className="h-full bg-[#ffd879] rounded-full transition-all duration-700" style={{ width: `${utilPct}%` }} />
                          </div>
                        </div>

                        {/* Metrics grid */}
                        <div className="p-4 grid grid-cols-4 gap-2">
                          <div className="bg-[#f9f9f9] p-2.5 text-center">
                            <p className="text-[10px] text-[#4a4a4a] mb-1">Invested</p>
                            <p className="text-xs font-bold text-[#1a1a1a]">{fmt(d.invested || 0)}</p>
                          </div>
                          <div className="bg-[#f9f9f9] p-2.5 text-center">
                            <p className="text-[10px] text-[#4a4a4a] mb-1">Current</p>
                            <p className="text-xs font-bold text-[#1a1a1a]">{fmt(d.current_value || 0)}</p>
                          </div>
                          <div className="bg-[#1a1a1a] p-2.5 text-center">
                            <p className="text-[10px] text-[#ffd87a] mb-1">MOIC</p>
                            <p className="text-sm font-bold text-white">{d.moic != null ? `${d.moic}x` : '—'}</p>
                          </div>
                          <div className="bg-[#f9f9f9] p-2.5 text-center">
                            <p className="text-[10px] text-[#4a4a4a] mb-1">IRR</p>
                            <p className={`text-xs font-bold ${d.irr != null ? 'text-emerald-600' : 'text-[#4a4a4a]'}`}>{d.irr != null ? `${d.irr}%` : '—'}</p>
                          </div>
                        </div>

                        {/* Unrealised P&L footer */}
                        <div className={`px-4 py-2.5 border-t border-[#f5f5f5] flex justify-between items-center ${isUp ? 'bg-emerald-50' : 'bg-[#fff8f5]'}`}>
                          <span className="text-[10px] text-[#4a4a4a] uppercase tracking-wide">Unrealised P&amp;L</span>
                          <span className={`text-sm font-bold ${isUp ? 'text-emerald-600' : 'text-[#c97a3a]'}`}>
                            {isUp ? '+' : ''}{fmt(gain)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ── Vintage distribution + Status donut ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

              {/* Vintage year bar chart */}
              <div className="overflow-hidden border border-[#e5e5e5] shadow-sm bg-white rounded-lg flex flex-col">
                <NavHead icon={Activity} title="Vintage Year Distribution" sub="Committed capital by fund year" />
                <div className="p-5">
                  {(() => {
                    const byVintage = pmCombined.reduce((acc, d) => {
                      if (!d.vintage) return acc;
                      const y = String(d.vintage);
                      if (!acc[y]) acc[y] = { committed: 0, count: 0 };
                      acc[y].committed += d.committed || d.invested || 0;
                      acc[y].count++;
                      return acc;
                    }, {});
                    const entries = Object.entries(byVintage).sort(([a], [b]) => a.localeCompare(b));
                    const maxVal = Math.max(...entries.map(([, v]) => v.committed), 1);
                    if (!entries.length) return (
                      <div className="text-center py-10 text-[#4a4a4a] text-sm">
                        No vintage data — add vintage years to funds to see distribution
                      </div>
                    );
                    return (
                      <div className="space-y-3">
                        {entries.map(([year, v]) => (
                          <div key={year} className="flex items-center gap-3">
                            <span className="text-xs font-semibold text-[#1a1a1a] w-10 shrink-0">{year}</span>
                            <div className="flex-1 bg-[#f5f5f5] h-8 relative overflow-hidden">
                              <div
                                className="absolute inset-y-0 left-0 bg-[#1a1a1a] flex items-center pl-3 transition-all duration-700"
                                style={{ width: `${(v.committed / maxVal) * 100}%` }}
                              >
                                <span className="text-[10px] font-bold text-[#ffd879] whitespace-nowrap">{fmt(v.committed)}</span>
                              </div>
                            </div>
                            <span className="text-xs text-[#4a4a4a] w-14 text-right shrink-0">{v.count} fund{v.count !== 1 ? 's' : ''}</span>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Status breakdown donut */}
              <div className="overflow-hidden border border-[#e5e5e5] shadow-sm bg-white rounded-lg flex flex-col">
                <NavHead icon={Shield} title="Portfolio Status" sub="Holdings by lifecycle stage" />
                <div className="p-5">
                  {pmCombined.length > 0 ? (() => {
                    const STATUS_COLORS = {
                      'Active': '#8ba888', 'Investing': '#8ba888',
                      'Harvesting': '#ffd87a', 'Pre-IPO': '#e8a838',
                      'Partial Exit': '#c97a3a', 'Realized': '#d4956a',
                      'Exited': '#d9c4a0', 'Unknown': '#b8a070',
                    };
                    const byStatus = pmCombined.reduce((acc, d) => {
                      const s = d.status || 'Unknown';
                      if (!acc[s]) acc[s] = { count: 0, value: 0 };
                      acc[s].count++;
                      acc[s].value += d.current_value || 0;
                      return acc;
                    }, {});
                    const statusEntries = Object.entries(byStatus).sort(([, a], [, b]) => b.value - a.value);
                    const donutData = statusEntries.map(([s, d]) => ({ name: s, value: d.value, color: STATUS_COLORS[s] || '#e5e5e5' })).filter(d => d.value > 0);
                    return (
                      <div className="flex gap-5 items-start">
                        {donutData.length > 0 && <div className="shrink-0"><DonutChart data={donutData} size={140} /></div>}
                        <div className="flex-1 space-y-2 min-w-0">
                          {statusEntries.map(([s, d]) => (
                            <div key={s} className="flex items-center justify-between bg-[#f9f9f9] px-3 py-2.5">
                              <div className="flex items-center gap-2 min-w-0">
                                <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: STATUS_COLORS[s] || '#e5e5e5' }} />
                                <span className="text-sm text-[#1a1a1a] truncate">{s}</span>
                              </div>
                              <div className="shrink-0 text-right ml-2">
                                <p className="text-xs font-bold text-[#1a1a1a]">{d.count} fund{d.count !== 1 ? 's' : ''}</p>
                                <p className="text-[10px] text-[#4a4a4a]">{fmt(d.value)}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })() : (
                    <div className="text-center py-10 text-[#4a4a4a] text-sm">No status data</div>
                  )}
                </div>
              </div>
            </div>

            {/* ── Startups & Deal Pipeline ── */}
            <div className="overflow-hidden border border-[#e5e5e5] shadow-sm bg-white rounded-lg flex flex-col">
              <NavHead amber icon={Rocket} title="Startups & Deal Pipeline"
                sub={`${pmDeals.length} deals · ${pmDeals.filter(d => !['closed','passed','lost'].includes(d.stage)).length} active in funnel`} />

              {/* 4 stat tiles */}
              <div className="grid grid-cols-2 md:grid-cols-4 border-b border-[#e5e5e5]">
                {[
                  { label: 'Total Deals',  val: pmDeals.length,                                                             sub: 'all time' },
                  { label: 'Active',       val: pmDeals.filter(d => !['closed','passed','lost'].includes(d.stage)).length,  sub: 'in funnel' },
                  { label: 'Closed / Won', val: pmDeals.filter(d => d.stage === 'closed').length,                          sub: 'investments made' },
                  { label: 'Passed',       val: pmDeals.filter(d => ['passed','lost'].includes(d.stage)).length,           sub: 'declined' },
                ].map(({ label, val, sub }) => (
                  <div key={label} className="p-5 text-center border-r border-[#e5e5e5] last:border-r-0">
                    <p className="text-3xl font-bold text-[#1a1a1a]">{val}</p>
                    <p className="text-xs font-semibold text-[#1a1a1a] mt-1">{label}</p>
                    <p className="text-xs text-[#4a4a4a]">{sub}</p>
                  </div>
                ))}
              </div>

              <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Pipeline funnel visual */}
                <div>
                  <p className="text-xs font-semibold text-[#4a4a4a] uppercase tracking-wider mb-4">Pipeline Funnel</p>
                  {dealsByStage.length > 0 ? (
                    <div className="space-y-2.5">
                      {dealsByStage.map(s => {
                        const maxCount = Math.max(...dealsByStage.map(x => x.count), 1);
                        const isWon  = s.key === 'closed';
                        const isLost = ['passed', 'lost'].includes(s.key);
                        const isHot  = ['term_sheet', 'closing'].includes(s.key);
                        const color  = isWon ? '#8ba888' : isLost ? '#c97a3a' : isHot ? '#ffd87a' : '#b8a070';
                        return (
                          <div key={s.key} className="flex items-center gap-3">
                            <span className="text-xs text-[#4a4a4a] w-28 shrink-0">{s.label}</span>
                            <div className="flex-1 bg-[#f5f5f5] h-9 relative overflow-hidden">
                              <div
                                className="absolute inset-y-0 left-0 flex items-center justify-between px-3 transition-all duration-700"
                                style={{ width: `${Math.max((s.count / maxCount) * 100, 6)}%`, backgroundColor: color }}
                              >
                                <span className="text-xs font-bold" style={{ color: isWon || isLost || (!isHot) ? 'white' : '#1a1a1a' }}>{s.count}</span>
                              </div>
                            </div>
                            <span className="text-xs font-bold text-[#1a1a1a] w-6 text-right shrink-0">{s.count}</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-10 text-[#4a4a4a] text-sm">No pipeline data yet</div>
                  )}
                </div>

                {/* Sector breakdown bar chart */}
                <div>
                  <p className="text-xs font-semibold text-[#4a4a4a] uppercase tracking-wider mb-4">Deals by Sector</p>
                  {(() => {
                    const bySector = pmDeals.reduce((acc, d) => {
                      const s = d.sector || 'Other';
                      acc[s] = (acc[s] || 0) + 1;
                      return acc;
                    }, {});
                    const entries = Object.entries(bySector).sort(([, a], [, b]) => b - a).slice(0, 8);
                    const max = Math.max(...entries.map(([, v]) => v), 1);
                    if (!entries.length) return <div className="text-center py-10 text-[#4a4a4a] text-sm">No sector data</div>;
                    return (
                      <div className="space-y-2.5">
                        {entries.map(([sector, count]) => (
                          <div key={sector} className="flex items-center gap-3">
                            <span className="text-xs text-[#4a4a4a] w-28 shrink-0 truncate">{sector}</span>
                            <div className="flex-1 bg-[#f5f5f5] h-9 relative overflow-hidden">
                              <div
                                className="absolute inset-y-0 left-0 bg-[#1a1a1a] flex items-center justify-end pr-3 transition-all duration-700"
                                style={{ width: `${(count / max) * 100}%` }}
                              >
                                <span className="text-xs font-bold text-[#ffd879]">{count}</span>
                              </div>
                            </div>
                            <span className="text-xs font-bold text-[#1a1a1a] w-6 text-right shrink-0">{count}</span>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>

          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

